import { useState, type ReactNode } from 'react';

import { Button, Icon, MonoLabel } from '@/design-system';
import type { DomainSetup, TenantDomain } from '@/entities/tenant';
import { errorMessage } from '@/shared/api';
import { formatDayTimeShort } from '@/shared/lib';

import { domainsCopy } from '../copy';
import { useRetryDomainSetup } from '../hooks';
import { CopyInstructionButton } from './CopyInstructionButton';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';
const MONO = 'font-[family-name:var(--font-mono)] text-[11px] break-all text-[var(--text-primary)]';

/** De stappen in de volgorde waarin ze gebeuren; `checking` is de laatste: in gebruik. */
const STEPS = ['verification', 'server', 'dns', 'certificate', 'checking'] as const;

type StepState = 'done' | 'current' | 'failed' | 'todo';

/** De stappen die het platform zelf zet, met wat er intussen gebeurt. */
const BUSY_STEPS = ['server', 'certificate', 'checking'] as const;

export interface DomainSetupPanelProps {
    domain: TenantDomain;
    /** Opnieuw proberen: alleen de eigenaar. */
    canManage: boolean;
}

/**
 * Hoe ver een geverifieerd rapportdomein is, voor het bedrijf zelf: de stappen, wat er nu
 * gebeurt of waarom het stopte, en het ene record dat het zelf moet zetten: met een zin
 * in gewone taal over wat dat record doet, en een knop die de instructie kopieert om
 * door te sturen naar wie de website beheert. Zonder Forge, nginx of Let's Encrypt: dat
 * zijn onze woorden, niet die van een keuringsbedrijf. Zet Smartkeur het domein met de
 * hand klaar, dan zegt het hoe lang dat kan duren.
 */
export function DomainSetupPanel({ domain, canManage }: DomainSetupPanelProps) {
    const { setup } = domain;
    const retry = useRetryDomainSetup();
    const [notice, setNotice] = useState<string | null>(null);

    const current = (STEPS as readonly string[]).indexOf(setup.step);

    if (!setup.automatic || current < 0) {
        return (
            <SetupBox>
                <p className="m-0 text-[12px] leading-[1.45] text-[var(--text-secondary)]">
                    {domainsCopy.setupManual}
                </p>
                {setup.dnsRecord ? (
                    <DnsRecord record={setup.dnsRecord} hostname={domain.hostname} />
                ) : null}
            </SetupBox>
        );
    }

    const message = messageFor(setup, domain.hostname);
    const showRecord =
        setup.dnsRecord !== null &&
        (setup.step === 'dns' || (setup.failed && setup.step === 'certificate'));

    return (
        <SetupBox>
            <ol
                aria-label={domainsCopy.setupStepsLabel(domain.hostname)}
                className="m-0 grid list-none gap-[5px] p-0"
            >
                {STEPS.map((step, index) => {
                    const state = stateOf(index, current, setup.failed);

                    return (
                        <li
                            key={step}
                            aria-current={
                                state === 'current' || state === 'failed' ? 'step' : undefined
                            }
                            className="flex items-center gap-2 text-[12px] leading-[1.45]"
                        >
                            <StepMark state={state} />
                            <span className={STEP_TEXT[state]}>{domainsCopy.setupSteps[step]}</span>
                            <span className="sr-only">{`, ${domainsCopy.setupStepState[state]}`}</span>
                        </li>
                    );
                })}
            </ol>

            {message ? (
                <p
                    className={`m-0 mt-[10px] text-[12px] leading-[1.45] ${
                        setup.failed ? 'text-[var(--warn-text)]' : 'text-[var(--text-secondary)]'
                    }`}
                >
                    {message}
                </p>
            ) : null}

            {showRecord && setup.dnsRecord ? (
                <DnsRecord record={setup.dnsRecord} hostname={domain.hostname} />
            ) : null}

            {!setup.failed && setup.step === 'dns' && setup.nextCheckAt ? (
                <p className={`m-0 mt-2 ${SMALL}`}>
                    {domainsCopy.nextCheck(formatDayTimeShort(setup.nextCheckAt))}
                </p>
            ) : null}

            {canManage && setup.canRetry ? (
                <div className="mt-[10px] flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="xs"
                        disabled={retry.isPending}
                        onClick={() => {
                            setNotice(null);
                            retry.reset();
                            retry.mutate(domain.uuid, {
                                onSuccess: () => setNotice(domainsCopy.retried(domain.hostname)),
                            });
                        }}
                    >
                        {retry.isPending
                            ? domainsCopy.retrying
                            : setup.failed
                              ? domainsCopy.retry
                              : domainsCopy.checkNow}
                    </Button>
                </div>
            ) : null}

            {notice ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}

            {retry.error ? (
                <p
                    role="alert"
                    className="m-0 mt-2 text-[12px] leading-[1.45] text-[var(--warn-text)]"
                >
                    {errorMessage(retry.error)}
                </p>
            ) : null}
        </SetupBox>
    );
}

const STEP_TEXT: Record<StepState, string> = {
    done: 'text-[var(--text-primary)]',
    current: 'font-medium text-[var(--text-primary)]',
    failed: 'font-medium text-[var(--warn-text)]',
    todo: 'text-[var(--text-tertiary-safe)]',
};

function stateOf(index: number, current: number, failed: boolean): StepState {
    if (index < current) {
        return 'done';
    }

    if (index === current) {
        return failed ? 'failed' : 'current';
    }

    return 'todo';
}

/**
 * Wat er nu gebeurt: bij een stop de reden, bij DNS wat je moet doen, en anders wat wij
 * aan het doen zijn — de reden van de server als die er is (een certificaat dat opnieuw
 * wordt aangevraagd), anders de vaste zin van die stap.
 */
function messageFor(setup: DomainSetup, hostname: string): string | null {
    if (setup.failed) {
        return setup.reason;
    }

    if (setup.step === 'dns') {
        return domainsCopy.dnsWaiting(hostname);
    }

    const busy = BUSY_STEPS.find((step) => step === setup.step);

    return setup.reason ?? (busy ? domainsCopy.setupBusy[busy] : null);
}

function StepMark({ state }: { state: StepState }) {
    return (
        <span
            aria-hidden="true"
            className="inline-flex h-[16px] w-[16px] shrink-0 items-center justify-center"
        >
            {state === 'done' ? <Icon name="check" size="sm" tone="brand" /> : null}
            {state === 'failed' ? <Icon name="info" size="sm" tone="warn" /> : null}
            {state === 'current' ? (
                <span className="h-[8px] w-[8px] rounded-full bg-[var(--accent)]" />
            ) : null}
            {state === 'todo' ? (
                <span className="h-[8px] w-[8px] rounded-full border border-[var(--border-control)]" />
            ) : null}
        </span>
    );
}

function DnsRecord({
    record,
    hostname,
}: {
    record: NonNullable<DomainSetup['dnsRecord']>;
    hostname: string;
}) {
    return (
        <>
            <p className={`m-0 mt-2 ${SMALL}`}>{domainsCopy.dnsExplain}</p>
            <MonoLabel size={10} tone="muted" className="mt-2 block">
                {domainsCopy.dnsTitle}
            </MonoLabel>
            <dl className="m-0 mt-[4px] grid grid-cols-[64px_minmax(0,1fr)] gap-x-[10px] gap-y-[3px]">
                <dt className="text-[12px] text-[var(--text-secondary)]">{domainsCopy.dnsType}</dt>
                <dd className={`m-0 ${MONO}`}>{record.type}</dd>
                <dt className="text-[12px] text-[var(--text-secondary)]">{domainsCopy.dnsName}</dt>
                <dd className={`m-0 ${MONO}`}>{record.name}</dd>
                <dt className="text-[12px] text-[var(--text-secondary)]">{domainsCopy.dnsValue}</dt>
                <dd className={`m-0 ${MONO}`}>{record.value}</dd>
            </dl>
            <p className={`m-0 mt-2 ${SMALL}`}>{domainsCopy.dnsTxtNote}</p>
            <CopyInstructionButton
                text={domainsCopy.cnameInstruction(
                    hostname,
                    record.type,
                    record.name,
                    record.value,
                )}
            />
        </>
    );
}

function SetupBox({ children }: { children: ReactNode }) {
    return (
        <div className="mt-[10px] rounded-[7px] border border-[var(--border-subtle)] bg-[var(--surface-subtle)] p-[10px]">
            {children}
        </div>
    );
}
